import { useEffect, useRef, useState } from 'react'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'

const STORAGE_KEY = 'support_session'

function loadSession() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || null
  } catch {
    return null
  }
}

function saveSession(s) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s))
}

function clearSession() {
  sessionStorage.removeItem(STORAGE_KEY)
}

function ChatIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  )
}

function CloseIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function SendIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  )
}

export default function SupportWidget() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [phase, setPhase] = useState('enterInfo') // enterInfo | chatting
  const [form, setForm] = useState({ name: '', email: '' })
  const [session, setSession] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [unread, setUnread] = useState(0)
  const messagesEndRef = useRef(null)
  const pollRef = useRef(null)
  const statusRef = useRef(null)

  // Restore session from localStorage on mount
  useEffect(() => {
    const stored = loadSession()
    if (stored) {
      setSession(stored)
      setPhase('chatting')
    }
    // Pre-fill from logged-in user
    if (user?.fullName) {
      setForm(prev => ({ ...prev, name: user.fullName }))
    }
  }, [user])

  // Scroll to bottom on new messages
  useEffect(() => {
    if (open) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
    }
  }, [messages, open])

  // Poll messages when chatting
  useEffect(() => {
    clearInterval(pollRef.current)
    if (phase !== 'chatting' || !session) {
      return
    }
    const poll = async () => {
      try {
        const { data } = await api.get(`/support/sessions/${session.id}/messages`, {
          headers: { 'X-Support-Token': session.sessionToken },
        })
        setMessages(data)
        if (!open) setUnread(prev => {
          const current = data.filter(m => m.senderType === 'ADMIN').length
          return current > (prev || 0) ? current : prev
        })
      } catch {}
    }
    poll()
    pollRef.current = setInterval(poll, 4000)
    return () => clearInterval(pollRef.current)
  }, [phase, session, open])

  // Check if session is closed
  useEffect(() => {
    clearInterval(statusRef.current)
    if (phase !== 'chatting' || !session) return
    const checkStatus = async () => {
      try {
        const { data } = await api.get(`/support/sessions/${session.id}`, {
          headers: { 'X-Support-Token': session.sessionToken },
        })
        if (data.status === 'CLOSED') {
          setSession(prev => ({ ...prev, closed: true }))
          clearSession()
        }
      } catch {}
    }
    statusRef.current = setInterval(checkStatus, 10000)
    return () => clearInterval(statusRef.current)
  }, [phase, session])

  const openWidget = () => {
    setOpen(true)
    setUnread(0)
  }

  const closeWidget = () => setOpen(false)

  const startChat = async (e) => {
    e.preventDefault()
    if (!form.name.trim() || !form.email.trim()) {
      setError('Ad və email daxil edin')
      return
    }
    setError('')
    setLoading(true)
    try {
      const { data } = await api.post('/support/sessions', {
        name: form.name.trim(),
        email: form.email.trim(),
      })
      const s = { id: data.id, sessionToken: data.sessionToken }
      setSession(s)
      saveSession(s)
      setPhase('chatting')
    } catch (err) {
      setError(err.response?.data?.message || 'Xəta baş verdi')
    } finally {
      setLoading(false)
    }
  }

  const sendMessage = async (e) => {
    e.preventDefault()
    if (!input.trim() || !session) return
    const text = input.trim()
    setInput('')
    const tempMsg = { id: Date.now(), senderType: 'USER', content: text, createdAt: new Date().toISOString() }
    setMessages(prev => [...prev, tempMsg])
    try {
      await api.post(`/support/sessions/${session.id}/messages`,
        { content: text },
        { headers: { 'X-Support-Token': session.sessionToken } }
      )
    } catch {
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id))
      setInput(text)
    }
  }

  const sessionClosed = session?.closed

  return (
    <>
      {/* Floating button — spring hover/press via CSS (.support-fab), no JS mouse events */}
      <button
        onClick={open ? closeWidget : openWidget}
        className="support-fab"
        style={{
          position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 9999,
          width: 52, height: 52, borderRadius: '50%',
          background: 'var(--accent)', color: '#0a0a0a',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(var(--accent-rgb),.35)',
        }}
        aria-label="Dəstək"
        aria-expanded={open}
      >
        {/* Animated icon swap: both always rendered, crossfade */}
        <span className={`support-fab-icon${open ? ' support-fab-icon--hidden' : ''}`} style={{ transform: open ? 'scale(0.5) rotate(-45deg)' : undefined }}>
          <ChatIcon />
        </span>
        <span className={`support-fab-icon${!open ? ' support-fab-icon--hidden' : ''}`} style={{ transform: !open ? 'scale(0.5) rotate(45deg)' : undefined }}>
          <CloseIcon />
        </span>
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2,
            background: '#ef4444', color: '#fff',
            borderRadius: '50%', width: 18, height: 18,
            fontSize: '.65rem', fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: open ? 0 : 1,
            transform: open ? 'scale(0.4)' : 'scale(1)',
            transition: 'opacity 120ms cubic-bezier(0.23,1,0.32,1), transform 120ms cubic-bezier(0.23,1,0.32,1)',
            pointerEvents: 'none',
          }}>{unread}</span>
        )}
      </button>

      {/* Dialog — always rendered, visibility controlled by CSS class (.support-dialog) */}
      <div
        className={`support-dialog${open ? ' support-dialog--open' : ''}`}
        style={{
          position: 'fixed', bottom: '5.5rem', right: '1.5rem', zIndex: 9998,
          width: 360, height: 500,
          borderRadius: '1rem',
          background: 'var(--surface)',
          border: '1px solid rgba(255,255,255,.1)',
          boxShadow: '0 8px 40px rgba(0,0,0,.45)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
        aria-hidden={!open}
      >
          {/* Header */}
          <div style={{
            padding: '1rem 1.25rem',
            background: 'rgba(var(--accent-rgb),.08)',
            borderBottom: '1px solid rgba(255,255,255,.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--text)' }}>Dəstək</div>
              <div style={{ fontSize: '.72rem', color: 'var(--muted)' }}>
                {phase === 'chatting' && !sessionClosed ? 'Onlayn — adətən tez cavablanır' : 'Sualınızı yazın'}
              </div>
            </div>
            <button onClick={closeWidget} aria-label="Bağla" style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--muted)', display: 'flex', padding: 4,
            }}>
              <CloseIcon />
            </button>
          </div>

          {/* Phase: enter info */}
          {phase === 'enterInfo' && (
            <form onSubmit={startChat} className="support-phase" style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              gap: '1rem', padding: '1.25rem',
            }}>
              <div style={{ fontSize: '.82rem', color: 'var(--muted)', lineHeight: 1.6 }}>
                Adınızı və emailinizi daxil edin, sonra mesajınızı yazın.
              </div>
              <input
                className="form-input"
                placeholder="Adınız"
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                required
              />
              <input
                className="form-input"
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                required
              />
              {error && <p style={{ margin: 0, fontSize: '.78rem', color: 'var(--danger)' }}>{error}</p>}
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? '...' : 'Başla'}
              </button>
            </form>
          )}

          {/* Phase: chatting */}
          {phase === 'chatting' && (
            <div className="support-phase" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ flex: 1, overflowY: 'auto', padding: '.85rem', display: 'flex', flexDirection: 'column', gap: '.55rem' }}>
                {/* Welcome message */}
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{
                    maxWidth: '80%', padding: '.55rem .85rem',
                    borderRadius: '12px 12px 12px 2px',
                    background: 'rgba(var(--accent-rgb),.1)',
                    border: '1px solid rgba(var(--accent-rgb),.18)',
                    fontSize: '.8rem', color: 'var(--text)', lineHeight: 1.5,
                  }}>
                    Salam! Sizə necə kömək edə bilərik? 👋
                  </div>
                </div>

                {messages.map(m => {
                  const isUser = m.senderType === 'USER'
                  return (
                    <div key={m.id} className="msg-bubble" style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                      <div style={{
                        maxWidth: '80%', padding: '.55rem .85rem',
                        borderRadius: isUser ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                        background: isUser ? 'rgba(var(--accent-rgb),.14)' : 'rgba(255,255,255,.06)',
                        border: `1px solid ${isUser ? 'rgba(var(--accent-rgb),.2)' : 'rgba(255,255,255,.1)'}`,
                        fontSize: '.8rem', color: 'var(--text)', lineHeight: 1.5,
                      }}>
                        {m.content}
                      </div>
                    </div>
                  )
                })}
                <div ref={messagesEndRef} />
              </div>

              {sessionClosed ? (
                <div style={{
                  padding: '.85rem 1.1rem', textAlign: 'center',
                  fontSize: '.78rem', color: 'var(--muted)',
                  borderTop: '1px solid rgba(255,255,255,.06)',
                }}>
                  Sessiya bağlandı
                </div>
              ) : (
                <form onSubmit={sendMessage} style={{
                  display: 'flex', gap: '.5rem', padding: '.75rem',
                  borderTop: '1px solid rgba(255,255,255,.06)',
                }}>
                  <input
                    className="form-input"
                    style={{ flex: 1, fontSize: '.82rem' }}
                    placeholder="Mesajınızı yazın..."
                    value={input}
                    onChange={e => setInput(e.target.value)}
                  />
                  <button
                    type="submit"
                    disabled={!input.trim()}
                    aria-label="Göndər"
                    style={{
                      background: 'var(--accent)', color: '#0a0a0a',
                      border: 'none', borderRadius: 'var(--radius-sm)',
                      width: 38, height: 38, cursor: 'pointer', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      opacity: input.trim() ? 1 : 0.4,
                    }}
                  >
                    <SendIcon size={16} />
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
    </>
  )
}
